import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sparkles, Lightbulb, Upload, Users, Heart } from 'lucide-react';
import { toast } from 'sonner';
import { GoogleLogin } from '@/components/auth/GoogleLogin';
import { FacebookLogin } from '@/components/auth/FacebookLogin';
import EmailConfirmationModal from '@/components/auth/EmailConfirmationModal';
import ErrorBoundary from '@/components/ErrorBoundary';
import PasswordStrengthIndicator from '@/components/ui/password-strength-indicator';
import { signupFormSchema, type SignupFormData } from '@/utils/passwordValidation';

const CustomSignUp = () => {
  const navigate = useNavigate();
  const { signUp, login, resendVerificationEmail } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [formData, setFormData] = useState<SignupFormData>({
    email: '',
    password: '',
    name: '',
  });
  const [formErrors, setFormErrors] = useState<Partial<SignupFormData>>({});

  // Debug modal state changes
  useEffect(() => {
    console.log('🔍 Modal state changed:', {
      showConfirmationModal,
      userEmail,
      timestamp: new Date().toISOString()
    });
    
    // Add additional debugging for modal visibility
    if (showConfirmationModal && userEmail) {
      console.log('✅ Modal should be visible now');
      // Check if modal actually renders in DOM after a brief delay
      setTimeout(() => {
        const modalElement = document.querySelector('[role="dialog"]');
        if (modalElement) {
          console.log('✅ Modal found in DOM:', modalElement);
        } else {
          console.error('❌ Modal not found in DOM despite state being true');
          // Trigger fallback notification
          toast.error(
            'Modal display issue detected. Redirecting to verification page.',
            { duration: 5000 }
          );
          navigate(`/verify-email?email=${encodeURIComponent(userEmail)}`);
        }
      }, 500);
    }
  }, [showConfirmationModal, userEmail, navigate]);

  const validateForm = (): boolean => {
    try {
      signupFormSchema.parse(formData);
      setFormErrors({});
      return true;
    } catch (error: any) {
      const errors: Partial<SignupFormData> = {};
      if (error.errors) {
        error.errors.forEach((err: any) => {
          if (err.path.length > 0) {
            errors[err.path[0] as keyof SignupFormData] = err.message;
          }
        });
      }
      setFormErrors(errors);
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form before proceeding
    if (!validateForm()) {
      toast.error('Please fix the form errors before continuing.');
      return;
    }
    
    setIsLoading(true);
    
    try {
      console.log('🚀 Starting signup process for email:', formData.email);
      
      // First create the account
      const { data, error } = await signUp(formData.email, formData.password, {
        full_name: formData.name,
      });
      
      if (error) {
        console.error('❌ Signup error occurred:', error);
        
        // Handle specific email sending error
        if (error.message?.includes('Error sending confirmation email')) {
          toast.error(
            'Account created but email confirmation failed. Please contact support or try signing in later.',
            { duration: 10000 }
          );
          console.error('Email service error - SMTP not configured:', error);
          
          // Redirect to login page after 3 seconds since account is created
          setTimeout(() => {
            navigate('/auth?message=account_created_check_email');
          }, 3000);
          return;
        }
        throw error;
      }

      console.log('✅ Signup successful, user data:', data?.user);

      // If signup successful and no email error, show success message
      if (data?.user) {
        if (data.user.email_confirmed_at) {
          console.log('🎉 Email already confirmed, logging in user');
          // Email was confirmed immediately (shouldn't happen with confirmation enabled)
          await login({
            email: formData.email,
            password: formData.password,
            name: formData.name || formData.email.split('@')[0],
          });
          toast.success('Account created and signed in successfully!');
          navigate('/profile');
        } else {
          console.log('📧 Email confirmation required, showing modal for:', formData.email);
          
          // Email confirmation required - this is the normal flow
          // Show the confirmation modal instead of toast + redirect
          setUserEmail(formData.email);
          
          // Add debugging for modal state
          console.log('🔄 Setting modal state: email =', formData.email, 'showModal = true');
          setShowConfirmationModal(true);
          
          // Add a fallback mechanism in case modal doesn't appear
          setTimeout(() => {
            if (!showConfirmationModal) {
              console.warn('⚠️ Modal may not have appeared, showing fallback toast');
              toast.success(
                `Account created! Please check ${formData.email} for verification email.`,
                { 
                  duration: 8000,
                  action: {
                    label: 'Open Email Verification',
                    onClick: () => navigate(`/verify-email?email=${encodeURIComponent(formData.email)}`)
                  }
                }
              );
            }
          }, 1000);
        }
      } else {
        console.error('❌ No user data returned from signup');
        throw new Error('Account creation failed - no user data returned');
      }
    } catch (error: any) {
      console.error('❌ Signup process failed:', error);
      toast.error(error.message || 'Failed to create account. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOAuthSignIn = async (provider: 'google' | 'facebook') => {
    if (provider === 'facebook') {
      // TODO: Implement Facebook sign-in
      console.log('Facebook signup coming soon');
      return;
    }
  };

  const handleResendEmail = async (email: string) => {
    console.log('📧 Attempting to resend verification email to:', email);
    try {
      await resendVerificationEmail(email);
      console.log('✅ Verification email resend successful');
      toast.success('Verification email sent! Please check your inbox.');
    } catch (error: any) {
      console.error('❌ Failed to resend verification email:', error);
      toast.error(error.message || 'Failed to resend email. Please try again.');
    }
  };

  const handleCloseModal = () => {
    console.log('🚪 Closing confirmation modal, redirecting to verification page');
    setShowConfirmationModal(false);
    // Redirect to dedicated verification page with email parameter
    navigate(`/verify-email?email=${encodeURIComponent(userEmail)}`);
  };

  const handleChangeEmail = () => {
    console.log('✏️ User wants to change email, closing modal and clearing form');
    setShowConfirmationModal(false);
    // Clear the email field and focus on it
    setFormData(prev => ({ ...prev, email: '' }));
    // Focus on email input (you might need to add a ref for this)
  };

  const handleInputChange = (field: keyof SignupFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Sign Up Form */}
      <div className="w-full lg:w-1/2 p-8 lg:p-12 flex items-center justify-center">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold">Create Your Account</h1>
            <p className="mt-2 text-muted-foreground">
              Join the community of holiday light enthusiasts
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Social Login Buttons */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="col-span-1">
                <GoogleLogin />
              </div>
              <div className="col-span-1">
                <FacebookLogin />
              </div>
            </div>

            {/* Divider */}
            <div className="relative mb-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-background text-muted-foreground">
                  Or sign up with email
                </span>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium mb-1">
                  Full Name
                </label>
                <Input
                  id="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={formErrors.name ? 'border-red-500 focus-visible:ring-red-500' : ''}
                />
                {formErrors.name && (
                  <p className="text-sm text-red-500 mt-1">{formErrors.name}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-1">
                  Email Address
                </label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={formErrors.email ? 'border-red-500 focus-visible:ring-red-500' : ''}
                />
                {formErrors.email && (
                  <p className="text-sm text-red-500 mt-1">{formErrors.email}</p>
                )}
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium mb-1">
                  Password
                </label>
                <Input
                  id="password"
                  type="password"
                  required
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  className={formErrors.password ? 'border-red-500 focus-visible:ring-red-500' : ''}
                />
                {formErrors.password && (
                  <p className="text-sm text-red-500 mt-1">{formErrors.password}</p>
                )}
                
                {/* Password Strength Indicator */}
                {formData.password && (
                  <div className="mt-2">
                    <PasswordStrengthIndicator 
                      password={formData.password}
                      showDetails={true}
                    />
                  </div>
                )}
              </div>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>

          {/* Sign In Link - visible on all screens */}
          <div className="text-center text-sm">
            <span className="text-muted-foreground">Already have an account?</span>{' '}
            <button
              onClick={() => navigate('/auth')}
              className="text-primary hover:underline font-medium"
            >
              Sign In
            </button>
          </div>
        </div>
      </div>

      {/* Right Side - Benefits */}
      <div className="hidden lg:flex w-1/2 bg-linear-to-br from-primary/10 to-accent/5 p-12">
        <div className="w-full max-w-lg mx-auto space-y-8">
          <div>
            <h2 className="text-2xl font-bold mb-6">
              Welcome to Light Hunt
            </h2>
            <p className="text-muted-foreground">
              Join our community and unlock all these amazing features
            </p>
          </div>

          <div className="space-y-6">
            <BenefitCard
              icon={<Lightbulb />}
              title="Showcase Your Display"
              description="Create a beautiful profile for your holiday light display and attract visitors from your area."
            />
            
            <BenefitCard
              icon={<Upload />}
              title="Share Your Sequences"
              description="Upload and sell your synchronized light sequences to other enthusiasts."
            />
            
            <BenefitCard
              icon={<Users />}
              title="Join the Community"
              description="Connect with fellow holiday light enthusiasts from around the world."
            />
            
            <BenefitCard
              icon={<Heart />}
              title="Support Charities"
              description="Use your holiday display to raise money for causes you care about."
            />
          </div>
        </div>
      </div>

      {/* Email Confirmation Modal */}
      <ErrorBoundary
        onError={(error, errorInfo) => {
          console.error('🚨 EmailConfirmationModal error:', { error, errorInfo });
          // Fallback to verification page if modal crashes
          navigate(`/verify-email?email=${encodeURIComponent(userEmail)}`);
        }}
        fallback={
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg p-6 max-w-md w-full text-center">
              <h2 className="text-lg font-semibold mb-2">Modal Error</h2>
              <p className="text-sm text-gray-600 mb-4">
                The confirmation modal couldn't load. Redirecting to verification page...
              </p>
            </div>
          </div>
        }
      >
        <EmailConfirmationModal
          isOpen={showConfirmationModal}
          email={userEmail}
          onClose={handleCloseModal}
          onResendEmail={handleResendEmail}
          onChangeEmail={handleChangeEmail}
        />
      </ErrorBoundary>
    </div>
  );
};

interface BenefitCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const BenefitCard: React.FC<BenefitCardProps> = ({ icon, title, description }) => {
  return (
    <div className="flex items-start space-x-4 p-4 rounded-lg bg-background/50 backdrop-blur-xs">
      <div className="text-primary">{icon}</div>
      <div>
        <h3 className="font-medium mb-1">{title}</h3>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
    </div>
  );
};

export default CustomSignUp; 